<script setup lang="ts">
import ContentView from './ContentView.vue'
import SpinnerOverlay from '@/components/common/SpinnerOverlay.vue'
import SalesRepContactButton from '@/components/common/SalesRepContactButton.vue'
import DivisionsWidget from '@/components/widgets/divisions-widget/DivisionsWidget.vue'
import PortfolioWidget from '@/components/widgets/portfolio-widget/PortfolioWidget.vue'
// import RadialBarChart from '@/components/widgets/common/RadialBarChart.vue'
import RadialBarChartTwo from '@/components/widgets/common/RadialBarChartTwo.vue'
import ArrotexLogo from '@/components/icons/ArrotexLogo.vue'
import ProductsTable from '@/components/widgets/portfolio-widget/ProductsTable.vue'
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useThemeStore, useValueDataStore } from '@/stores'
import { transformDataToRadialChartFormat } from '@/components/common/common'
import ArrotexDots from '@/components/icons/ArrotexDots.vue'
import ExportButton from '@/components/common/ExportButton.vue'
import ExternalLinks from '@/components/common/ExternalLinks.vue'
import ErrorView from './ErrorView.vue'
import type { OpportunityProducts } from '@/data'
import SpecialtyBrandsTable from '@/components/widgets/portfolio-widget/SpecialtyBrandsTable.vue'
// import DataRefreshDatesButton from '@/components/common/DataRefreshDatesButton.vue'

const themeStore = useThemeStore()
const { themeColors } = themeStore

const colorProps = computed(() => ({
  primary: themeColors.colorArrotex,
  secondary: themeColors.colorArrotexDark,
  tertiary: themeColors.colorText
}))

const { isValueDataStoreLoading, valueData, isError } = storeToRefs(useValueDataStore())

const selectedPortfolio = ref(valueData.value.arrotex.portfolioValues[0]!.portfolioName)

const arrotexTransformedData = ref(
  transformDataToRadialChartFormat(valueData.value.arrotex, 'arrotex')
)

const selectedOpportunityProducts = computed<OpportunityProducts>(() => {
  if (!selectedPortfolio.value) return []

  return (
    valueData.value.arrotex.portfolioValues
      .find((portfolio: any) => portfolio.portfolioName === selectedPortfolio.value)
      ?.opportunityProducts?.map((p) => {
        if (['Generics', 'Biologics'].includes(selectedPortfolio.value)) {
          return {
            sku: p.sku,
            opportunityUnits: p.opportunityUnits,
            opportunityValue: p.opportunityValue,
            loyalty: p.loyalty,
            substitution: p.substitution
          }
        } else if (selectedPortfolio.value === 'Specialty Brands') {
          return {
            sku: p.sku,
            opportunityUnits: p.opportunityUnits,
            opportunityValue: p.opportunityValue,
            franchiseShare: p.franchiseShare,
            substitution: p.substitution
          }
        }
        return null // Explicitly return null for unmatched cases
      })
      ?.filter((p): p is NonNullable<typeof p> => p !== null) ?? [] // Remove null values
  )
})

function selectPortfolio(portfolioName: string) {
  selectedPortfolio.value =
    valueData.value.arrotex.portfolioValues.find(
      (portfolio: any) => portfolio.portfolioName === portfolioName
    )?.portfolioName || ''
}

watch(valueData, () => {
  arrotexTransformedData.value = transformDataToRadialChartFormat(
    valueData.value.arrotex,
    'arrotex'
  )
  selectedPortfolio.value = valueData.value.arrotex.portfolioValues[0]!.portfolioName
})
</script>

<template>
  <SpinnerOverlay v-if="isValueDataStoreLoading" />
  <ErrorView v-else-if="isError" />
  <ContentView v-else>
    <template #top>
      <!-- <DataRefreshDatesButton/> -->
      <ExternalLinks />
      <!-- <ArrotexLogo width="175px" height="50px" :fill="themeColors.colorText" /> -->
      <SalesRepContactButton label="Contact your Arrotex Rep" type="arrotex" />
      <ExportButton />
    </template>
    <template #middle>
      <div class="arrotex-value-middle-left">
        <DivisionsWidget
          :isMain="false"
          :totalValue="valueData.arrotex.value"
          :currentQuarterValue="valueData.arrotex.valueCurrentQuarter"
          :opportunityValue="valueData.arrotex.opportunity"
        >
          <template #value-title>
            <ArrotexLogo width="7.8125rem" height="3.125rem" :fill="themeColors.colorText" />
          </template>
          <template #chart>
            <RadialBarChartTwo :data="arrotexTransformedData" :showLegend="true" />
            <!-- <RadialBarChart :data="arrotexTransformedData" /> -->
          </template>
          <template #opportunity-title><span class="title">Arrotex Opportunity</span></template>
        </DivisionsWidget>

        <PortfolioWidget
          :data="valueData.arrotex.portfolioValues"
          :colors="colorProps"
          :selectPortfolio="selectPortfolio"
        >
          <template #logo>
            <ArrotexDots width="2.875rem" height="2.875rem" />
          </template>
        </PortfolioWidget>
      </div>
      <div class="arrotex-value-middle-right">
        <SpecialtyBrandsTable v-if="selectedPortfolio === 'Specialty Brands'" :glow="true" />
        <ProductsTable
          v-else
          :data="selectedOpportunityProducts!"
          :portfolioName="selectedPortfolio"
          :color="themeColors.colorArrotex"
          :glow="true"
        >
        </ProductsTable>
      </div>
    </template>
  </ContentView>
</template>

<style scoped>
.arrotex-value-middle-left,
.arrotex-value-middle-right {
  display: flex;
  flex: 0.5;
  flex-direction: row;
  justify-content: space-around;
  gap: 2.5%;
}

.title {
  font-size: var(--font-size-h2);
  font-weight: 600;
}
</style>
